import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Users, Search, ArrowLeft, ArrowUpDown } from "lucide-react";

export default function SuppliersDirectory() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("supplier_name");
  const [sortDir, setSortDir] = useState("asc");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/suppliers-directory");
        setRows(data || []);
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = q ? rows.filter((r) => [r.supplier_name, r.supplier_vat_id, r.supplier_country, r.supplier_city]
      .some((v) => (v || "").toLowerCase().includes(q))) : rows.slice();
    list.sort((a, b) => {
      const av = (a[sortBy] || "").toString().toLowerCase();
      const bv = (b[sortBy] || "").toString().toLowerCase();
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === "asc" ? cmp : -cmp;
    });
    return list;
  }, [rows, query, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const cur = Math.min(page, totalPages);
  const pageRows = filtered.slice((cur - 1) * pageSize, cur * pageSize);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir("asc"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-10" data-testid="suppliers-page">
      <Link to="/app/invoices" className="inline-flex items-center gap-1 text-sm text-[#1D4ED8] hover:underline mb-4" data-testid="back-invoices">
        <ArrowLeft className="w-3.5 h-3.5" /> Torna alla Lista Autofatture XML
      </Link>

      <div className="mb-8 flex items-start gap-3">
        <div className="w-11 h-11 rounded-full bg-blue-50 grid place-items-center shrink-0">
          <Users className="w-5 h-5 text-[#1D4ED8]" />
        </div>
        <div>
          <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">Rubrica Fornitori</h1>
          <p className="text-zinc-600 text-sm mt-0.5">I fornitori esteri delle tue autofatture, creati e aggiornati automaticamente dalle fatture che carichi.</p>
        </div>
      </div>

      <div className="rounded-xl border border-[#E8E4D9] bg-white p-4 sm:p-6" data-testid="suppliers-card">
        <div className="mb-4">
          <h2 className="font-display font-semibold text-lg">I tuoi fornitori ({filtered.length})</h2>
          <p className="text-xs text-[#1D4ED8] mt-0.5">Le schede vengono create in automatico quando carichi una fattura. Puoi cercare per nome, P.IVA, paese o città.</p>
        </div>

        <div className="mb-4">
          <label className="block text-xs font-medium text-[#1D4ED8] mb-1">Cerca</label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input value={query} onChange={(e) => { setQuery(e.target.value); setPage(1); }}
              placeholder="Cerca per nome, P.IVA, paese o città…" data-testid="suppliers-search"
              className="w-full h-10 pl-10 pr-3 rounded-md border border-[#E8E4D9] bg-white focus:border-zinc-900 focus:ring-2 focus:ring-zinc-900/10 outline-none text-sm" />
          </div>
        </div>

        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-zinc-500 border-b border-[#E8E4D9]">
                <Th onClick={() => toggleSort("supplier_name")} label="Denominazione" active={sortBy === "supplier_name"} dir={sortDir} />
                <Th onClick={() => toggleSort("supplier_vat_id")} label="Partita IVA" active={sortBy === "supplier_vat_id"} dir={sortDir} />
                <Th onClick={() => toggleSort("supplier_country")} label="Paese" active={sortBy === "supplier_country"} dir={sortDir} />
                <Th onClick={() => toggleSort("supplier_city")} label="Città" active={sortBy === "supplier_city"} dir={sortDir} />
                <Th onClick={() => toggleSort("invoice_count")} label="Fatture" active={sortBy === "invoice_count"} dir={sortDir} align="right" />
                <Th onClick={() => toggleSort("last_invoice_date")} label="Ultima fattura" active={sortBy === "last_invoice_date"} dir={sortDir} align="right" />
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center text-sm text-zinc-500 py-10">Caricamento…</td></tr>
              ) : pageRows.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-sm text-zinc-500 py-10" data-testid="suppliers-empty">
                  Nessun fornitore in rubrica. Le schede vengono create automaticamente quando carichi le fatture.
                </td></tr>
              ) : pageRows.map((r, i) => (
                <tr key={r.id} className="border-b border-[#E8E4D9]/70 hover:bg-[#F5F2EA]" data-testid={`supplier-row-${i}`}>
                  <td className="py-3 px-4 font-medium text-zinc-900">{r.supplier_name}</td>
                  <td className="py-3 px-4 font-mono text-xs text-zinc-700">{r.supplier_vat_id || "—"}</td>
                  <td className="py-3 px-4 font-mono text-xs text-zinc-700">{r.supplier_country || "—"}</td>
                  <td className="py-3 px-4 text-zinc-700">{r.supplier_city || "—"}</td>
                  <td className="py-3 px-4 text-right font-mono">{r.invoice_count}</td>
                  <td className="py-3 px-4 text-right font-mono text-zinc-600">{r.last_invoice_date || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-4 text-xs text-zinc-600 flex-wrap gap-3">
          <span>{filtered.length} righe totali</span>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2">
              Righe per pagina
              <select value={pageSize} onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}
                className="h-8 px-2 rounded-md border border-[#E8E4D9] bg-white" data-testid="suppliers-page-size">
                {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <span>Pagina {cur} di {totalPages}</span>
            <div className="flex items-center gap-1">
              <PageBtn disabled={cur === 1} onClick={() => setPage(1)}>«</PageBtn>
              <PageBtn disabled={cur === 1} onClick={() => setPage(cur - 1)}>‹</PageBtn>
              <PageBtn disabled={cur === totalPages} onClick={() => setPage(cur + 1)}>›</PageBtn>
              <PageBtn disabled={cur === totalPages} onClick={() => setPage(totalPages)}>»</PageBtn>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Th({ label, onClick, active, dir, align = "left" }) {
  return (
    <th onClick={onClick}
      className={`py-2 px-4 font-medium cursor-pointer select-none whitespace-nowrap ${align === "right" ? "text-right" : "text-left"}`}>
      <span className={`inline-flex items-center gap-1 ${active ? "text-zinc-900" : "text-zinc-500"}`}>
        {label} <ArrowUpDown className={`w-3 h-3 ${active ? "text-[#1D4ED8]" : ""}`} />
      </span>
    </th>
  );
}

function PageBtn({ children, disabled, onClick }) {
  return (
    <button onClick={onClick} disabled={disabled}
      className="w-8 h-8 rounded-md border border-[#E8E4D9] bg-white text-zinc-700 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
      {children}
    </button>
  );
}
